/**
 * Secure Authentication Service
 * Handles authentication, token management, and security best practices
 */

class AuthService {
  constructor() {
    this.baseURL = '';
    this.refreshEndpoint = '/api/auth/refresh';
    this.logoutEndpoint = '/api/auth/logout';
    this.isRefreshing = false;
    this.failedQueue = [];
    this.initialized = false;
  }

  initialize() {
    if (!this.initialized) {
      this.baseURL = import.meta.env.VITE_API_URL || '';
      this.initialized = true;
    }
  }

  /**
   * Process failed requests queue after token refresh
   */
  processQueue(error, token = null) {
    this.failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    
    this.failedQueue = [];
  }

  /**
   * Refresh authentication token
   */
  async refreshToken() {
    this.initialize();
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      const response = await fetch(`${this.baseURL}${this.refreshEndpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      this.processQueue(null);
      return true;
    } catch (error) {
      this.processQueue(error, null);
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Get access token from storage
   */
  getAccessToken() {
    // Try to get token from localStorage first
    const token = localStorage.getItem('access_token');
    if (token) return token;
    
    // Try to get token from sessionStorage
    const sessionToken = sessionStorage.getItem('access_token');
    if (sessionToken) return sessionToken;
    
    return null;
  }

  /**
   * Make authenticated request with automatic token refresh
   */
  async authenticatedRequest(url, options = {}) {
    this.initialize();
    const token = this.getAccessToken();
    
    // Construct full URL if it's a relative path
    const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`;
    
    // Check if we're on the login page - don't try to refresh token there
    const isOnLoginPage = window.location.pathname === '/agent/login' || 
                          window.location.pathname === '/admin/login';
    
    const requestOptions = {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(fullUrl, requestOptions);

      // If unauthorized, try to refresh token (but not on login page)
      if (response.status === 401) {
        // In dev mock mode there are no real tokens — return the 401 as-is so
        // callers can handle it gracefully without triggering a redirect loop.
        if (import.meta.env.DEV && import.meta.env.VITE_DEV_MOCK_USER) {
          return response;
        }

        // On login page, just return the 401 response without trying to refresh
        if (isOnLoginPage) {
          return response;
        }

        try {
          await this.refreshToken();
          // Retry the original request
          return await fetch(fullUrl, requestOptions);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          this.handleAuthFailure();
          throw refreshError;
        }
      }

      return response;
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        // Network error
        throw new Error('Network error. Please check your connection.');
      }
      throw error;
    }
  }

  /**
   * Handle authentication failure
   */
  handleAuthFailure() {
    // In dev mock mode there are no real tokens — suppress redirects so the UI
    // can render with mock data. Components show empty states for failed API calls.
    if (import.meta.env.DEV && import.meta.env.VITE_DEV_MOCK_USER) {
      return;
    }

    // Clear any stored user data
    localStorage.removeItem('user');
    sessionStorage.clear();

    // Clear cookies
    this.clearAuthCookies();

    // Redirect to login
    const isAdmin = window.location.pathname.startsWith('/admin');
    const loginPath = isAdmin ? '/admin/login' : '/agent/login';
    window.location.href = loginPath;
  }

  /**
   * Clear authentication cookies
   */
  clearAuthCookies() {
    const cookies = [
      'access_token',
      'refresh_token',
      'session_token'
    ];

    cookies.forEach(cookieName => {
      // Clear for current domain
      document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      // Clear for .parkproit.com domain
      document.cookie = `${cookieName}=; domain=.parkproit.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=None; Secure`;
      // Clear for parkproit.com domain
      document.cookie = `${cookieName}=; domain=parkproit.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=None; Secure`;
    });
  }

  /**
   * Logout user
   */
  async logout() {
    this.initialize();
    try {
      // Call logout endpoint
      await fetch(`${this.baseURL}${this.logoutEndpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      // Always clear local data and redirect
      this.handleAuthFailure();
    }
  }

  /**
   * Get current user data
   */
  async getCurrentUser() {
    this.initialize();
    try {
      const response = await this.authenticatedRequest(`${this.baseURL}/api/me`);
      
      if (!response.ok) {
        if (response.status === 401) {
          return null; // Not authenticated
        }
        // Log more details for debugging
        const errorText = await response.text().catch(() => 'Unable to read error response');
        console.error(`Failed to fetch user data: ${response.status} ${response.statusText}`, errorText);
        throw new Error(`Failed to fetch user data: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      // Only log if it's not a network/CORS error (those are expected when not authenticated)
      if (error.message && !error.message.includes('Network error') && !error.message.includes('fetch')) {
        console.error('Error fetching user:', error);
      }
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated() {
    try {
      const user = await this.getCurrentUser();
      return !!user;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get safe user data for localStorage
   */
  getSafeUserData(userData) {
    if (!userData) return null;
    
    return {
      userid: userData.userid,
      firstname: userData.firstname,
      lastname: userData.lastname,
      role: userData.role,
      agency_id: userData.agency_id,
      agencyName: userData.agencyName || userData.agency_name,
      // Add other non-sensitive fields as needed
    };
  }

  /**
   * Validate user session
   */
  async validateSession() {
    this.initialize();
    try {
      const response = await fetch(`${this.baseURL}/api/auth/validate`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return response.ok;
    } catch (error) {
      console.error('Session validation failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export default new AuthService();
